import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, ExternalLink } from "lucide-react";
import { useSite } from "../context/SiteContext";
import {
  PEOPLE,
  CardFront,
  CardBack,
  MidnightCardFront,
  MidnightCardBack,
  OnyxCardFront,
  OnyxCardBack,
  ForestCardFront,
  ForestCardBack,
  Flyer,
  SocialPost,
  PostcardFront,
  PostcardBack,
  WindowDecal,
  EmailSignaturePreview,
} from "./Marketing";

// — A single asset on its own "page" with caption —
function ReviewItem({ label, dimensions, notes, scale = 1, children }) {
  return (
    <article
      className="review-item bg-white border border-[#E2DCD0] mb-10"
      style={{ pageBreakInside: "avoid", breakInside: "avoid" }}
    >
      <header className="px-6 md:px-10 py-5 border-b border-[#E2DCD0] flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <p className="overline text-[#9F5C45]">{label}</p>
          <p className="font-serif text-2xl text-[#1C1917] mt-1">{dimensions}</p>
        </div>
        {notes && (
          <p className="text-xs text-[#57534E] sm:max-w-sm sm:text-right">{notes}</p>
        )}
      </header>
      <div className="p-6 md:p-10 flex items-center justify-center overflow-hidden bg-[#FAF7F0]">
        <div style={{ transform: `scale(${scale})`, transformOrigin: "center center" }}>
          {children}
        </div>
      </div>
    </article>
  );
}

export default function MarketingReview() {
  const { site } = useSite();
  const brand = site?.brand_name || "Let Go Wellness";
  const tagline = "a quiet space to let go.";
  const logoUrl = site?.brand_logo_url || "";
  const phone = site?.phone || "203-437-8193";
  const email = site?.email || "Letgowellness1@gmail.com";
  const addr1 = site?.address_line1 || "808 Highland Ave, 1st Fl";
  const addr2 = site?.address_line2 || "Waterbury, CT 06708";
  const hoursWd = site?.hours_weekday || "Mon–Fri · 8am — 8pm";
  const hoursWe = site?.hours_weekend || "Sat · 9am — 4pm";
  const services = site?.services || [];

  return (
    <main className="min-h-screen bg-[#F4F1EA] text-[#1C1917] font-sans antialiased" data-testid="marketing-review-page">
      {/* Slim review header — no main site nav, this is a focused review view */}
      <header className="bg-[#FDFBF7] border-b border-[#E2DCD0] sticky top-0 z-40 print:hidden">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link
              to="/marketing"
              data-testid="review-back"
              className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#3A5A40] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to kit
            </Link>
            <span className="hidden sm:inline text-[#E2DCD0]">·</span>
            <div>
              <p className="overline text-[#9F5C45]">Marketing review</p>
              <p className="font-serif text-lg text-[#1C1917] leading-none mt-1">All assets · {brand}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/marketing"
              data-testid="review-to-downloads"
              className="inline-flex items-center gap-2 px-4 py-2 btn-ghost text-xs tracking-[0.18em] uppercase"
            >
              Downloads <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <button
              onClick={() => window.print()}
              data-testid="review-print-all"
              className="inline-flex items-center gap-2 px-4 py-2 btn-primary text-xs tracking-[0.18em] uppercase"
            >
              <Printer className="w-3.5 h-3.5" /> Print all
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-16 pb-10 print:hidden">
        <p className="overline text-[#9F5C45] mb-4">Review · before you print</p>
        <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1C1917] max-w-3xl">
          Everything in one place — at real size.
        </h1>
        <p className="text-[#57534E] mt-4 max-w-2xl text-sm md:text-base leading-relaxed">
          Scroll through your full marketing kit below. Each asset is shown at its true print
          dimensions. Hit <strong>Print all</strong> in the header to save the whole review as a PDF,
          or use the per-asset Download buttons on the previous page.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 pb-24">
        {/* — BUSINESS CARDS — */}
        {PEOPLE.map((person) => (
          <React.Fragment key={person.key}>
            <ReviewItem
              label={`Business card · ${person.name} · Front`}
              dimensions={`3.5″ × 2″ · ${person.creds}`}
              notes="Heavy stock recommended (16pt+ matte). Print at 100% scale, then trim."
            >
              <CardFront
                person={person}
                brand={brand}
                tagline={tagline}
                logoUrl={logoUrl}
                phone={phone}
                email={email}
                addr1={addr1}
                addr2={addr2}
              />
            </ReviewItem>
            <ReviewItem
              label={`Business card · ${person.name} · Back`}
              dimensions={`3.5″ × 2″ · QR + tagline`}
              notes="QR code scans to letgowelllness.com — verified."
            >
              <CardBack person={person} brand={brand} />
            </ReviewItem>
            <ReviewItem
              label={`Midnight card · ${person.name} · Front`}
              dimensions={`2″ × 3.5″ · Portrait · Navy & Rose Gold`}
              notes="Alternate luxe variant. Print on dark matte stock with optional foil."
            >
              <MidnightCardFront brand={brand} logoUrl={logoUrl} />
            </ReviewItem>
            <ReviewItem
              label={`Midnight card · ${person.name} · Back`}
              dimensions={`2″ × 3.5″ · Portrait · Contact details`}
              notes="Rose-gold hairline frame. Designed for the editorial-luxury crowd."
            >
              <MidnightCardBack
                person={person}
                brand={brand}
                phone={phone}
                email={email}
                addr1={addr1}
                addr2={addr2}
              />
            </ReviewItem>
            <ReviewItem
              label={`Onyx card · ${person.name} · Front`}
              dimensions={`2″ × 3.5″ · Portrait · Black + Gold Foil`}
              notes="Specify gold foil stamping with printer for real shimmer."
            >
              <OnyxCardFront brand={brand} logoUrl={logoUrl} />
            </ReviewItem>
            <ReviewItem
              label={`Onyx card · ${person.name} · Back`}
              dimensions={`2″ × 3.5″ · Portrait · Contact details`}
              notes="Gold accents on deep black. Premium night-out card."
            >
              <OnyxCardBack
                person={person}
                brand={brand}
                phone={phone}
                email={email}
                addr1={addr1}
                addr2={addr2}
              />
            </ReviewItem>
            <ReviewItem
              label={`Forest card · ${person.name} · Front`}
              dimensions={`2″ × 3.5″ · Portrait · Sage + Gold Foil`}
              notes="Brand sage with gold accents — warm, ceremonial."
            >
              <ForestCardFront brand={brand} logoUrl={logoUrl} />
            </ReviewItem>
            <ReviewItem
              label={`Forest card · ${person.name} · Back`}
              dimensions={`2″ × 3.5″ · Portrait · Contact details`}
              notes="Sage + gold-foil. The clinic signature in card form."
            >
              <ForestCardBack
                person={person}
                brand={brand}
                phone={phone}
                email={email}
                addr1={addr1}
                addr2={addr2}
              />
            </ReviewItem>
          </React.Fragment>
        ))}

        {/* — FLYER — */}
        <ReviewItem
          label="Flyer · Waiting-room & community"
          dimensions="8.5″ × 11″ (US Letter)"
          notes="Drop into coffee shops, primary-care offices, gyms."
          scale={0.6}
        >
          <Flyer
            brand={brand}
            tagline={tagline}
            logoUrl={logoUrl}
            phone={phone}
            email={email}
            addr1={addr1}
            addr2={addr2}
            hoursWd={hoursWd}
            hoursWe={hoursWe}
            services={services}
          />
        </ReviewItem>

        {/* — SOCIAL POST — */}
        <ReviewItem
          label="Social media post · Square"
          dimensions="1080 × 1080 px"
          notes="Instagram feed, Facebook, LinkedIn."
          scale={0.85}
        >
          <SocialPost
            brand={brand}
            tagline={tagline}
            logoUrl={logoUrl}
            phone={phone}
          />
        </ReviewItem>

        {/* — POSTCARD — */}
        <ReviewItem
          label="Referral postcard · Front"
          dimensions="6″ × 4″ (USPS standard)"
          notes="Front speaks to the heart. Hand to patients at checkout."
        >
          <PostcardFront brand={brand} tagline={tagline} logoUrl={logoUrl} />
        </ReviewItem>
        <ReviewItem
          label="Referral postcard · Back · Address side"
          dimensions="6″ × 4″"
          notes="USPS-compliant: stamp box + 4-line address area + QR code."
        >
          <PostcardBack brand={brand} addr1={addr1} addr2={addr2} phone={phone} />
        </ReviewItem>

        {/* — WINDOW DECAL — */}
        <ReviewItem
          label="Window decal · Clinic door / glass"
          dimensions="6″ × 6″ (rounded square)"
          notes="Send PNG to any local sign shop, or order from StickerYou."
        >
          <WindowDecal brand={brand} logoUrl={logoUrl} />
        </ReviewItem>

        {/* — EMAIL SIGNATURES — */}
        {PEOPLE.map((person) => (
          <ReviewItem
            key={`sig-${person.key}`}
            label={`Email signature · ${person.name}`}
            dimensions="Rich HTML · works in Gmail, Outlook, Apple Mail"
            notes="Use Copy button on the previous page to install."
          >
            <div className="bg-white p-6 border border-[#E2DCD0] min-w-[420px]">
              <EmailSignaturePreview
                person={person}
                brand={brand}
                logoUrl={logoUrl}
                phone={phone}
                email={email}
                addr1={addr1}
                addr2={addr2}
              />
            </div>
          </ReviewItem>
        ))}

        <div className="mt-16 pt-10 border-t border-[#E2DCD0] text-center">
          <p className="font-serif italic text-[#57534E] text-lg">
            That&apos;s the full kit. Looks good? Head back and download what you need.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Link
              to="/marketing"
              className="inline-flex items-center gap-2 px-5 py-3 btn-primary text-xs tracking-[0.22em] uppercase"
            >
              Open downloads
            </Link>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-5 py-3 btn-ghost text-xs tracking-[0.22em] uppercase"
            >
              <Printer className="w-4 h-4" /> Print this review
            </button>
          </div>
        </div>
      </section>

      {/* Print rules: one asset per page, no chrome */}
      <style>{`
        @media print {
          @page { margin: 0.5in; }
          body { background: #FFFFFF !important; }
          header, .print\\:hidden { display: none !important; }
          .review-item {
            page-break-inside: avoid;
            break-inside: avoid;
            page-break-after: always;
            break-after: page;
            border: none !important;
            margin: 0 !important;
          }
          .review-item header { display: none !important; }
        }
      `}</style>
    </main>
  );
}
